import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { financeService } from '../services/financeService';
import { withCache } from '../services/cacheService';
import { analyticsService } from '../services/analyticsService';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  LinearProgress,
  Skeleton,
  Avatar,
  Fade,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  Category as CategoryIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import SuppliersTab from '../components/SuppliersTab';
import BanksTab from '../components/BanksTab';
import SubclassificationsTab from '../components/SubclassificationsTab';

const FinanceManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentTab, setCurrentTab] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [globalTotals, setGlobalTotals] = useState({ total_revenue: 0, total_costs: 0, total_expenses: 0, unverified_count: 0 });
  const [assets, setAssets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionAttachments, setTransactionAttachments] = useState([]);
  const [transactionAttachmentsLoading, setTransactionAttachmentsLoading] = useState(false);
  const [transactionAttachmentsUploading, setTransactionAttachmentsUploading] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [transactionToVerify, setTransactionToVerify] = useState(null);
  const [verifyingTransaction, setVerifyingTransaction] = useState(false);
  const [savingTransaction, setSavingTransaction] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState({ product: '', asset: '', supplier: '', bank: '', subclassification: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [productFilterLabel, setProductFilterLabel] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [bankFilterLabel, setBankFilterLabel] = useState('');
  const [subclassificationFilter, setSubclassificationFilter] = useState('');
  const [subclassificationFilterLabel, setSubclassificationFilterLabel] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exportingTransactions, setExportingTransactions] = useState(false);
  const [enums, setEnums] = useState({
    transactionTypes: [],
    transactionClassifications: []
  });
  const [formData, setFormData] = useState({
    transaction_date: new Date(),
    due_date: null,
    payment_date: null,
    payment_status: '',
    amount: '',
    transaction_type: '',
    classification: '',
    description: '',
    product_id: '',
    asset_id: '',
    supplier_id: '',
    bank_id: '',
    subclassification_id: '',
    notes: ''
  });
  const { showNotification } = useNotification();
  const { user, isSuperAdmin, hasPermission } = useAuth();

  const canReadTransactionAttachments = Boolean(hasPermission && hasPermission('finance.transaction_attachments.read'));
  const canCreateTransactionAttachments = Boolean(hasPermission && hasPermission('finance.transaction_attachments.create'));
  const canDeleteTransactionAttachments = Boolean(hasPermission && hasPermission('finance.transaction_attachments.delete'));

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return '-';
    const value = Number(bytes);
    if (value < 1024) return `${value} B`;
    const kb = value / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(1)} GB`;
  };

  const loadTransactionAttachments = async (transactionId) => {
    if (!transactionId) return;
    if (!canReadTransactionAttachments) {
      setTransactionAttachments([]);
      return;
    }
    try {
      setTransactionAttachmentsLoading(true);
      const res = await financeService.getTransactionAttachments(transactionId);
      setTransactionAttachments(res?.data || []);
    } catch (err) {
      setTransactionAttachments([]);
      showNotification(err.message || 'Erro ao carregar anexos', 'error');
    } finally {
      setTransactionAttachmentsLoading(false);
    }
  };

  const handleUploadTransactionFiles = async (event) => {
    const transactionId = selectedTransaction?.id;
    if (!transactionId) return;

    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    try {
      setTransactionAttachmentsUploading(true);

      for (const file of files) {
        const presign = await financeService.presignTransactionAttachment(transactionId, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size
        });

        await financeService.uploadToPresignedUrl(presign.upload_url, presign.headers || {}, file);
        await financeService.completeTransactionAttachment(transactionId, presign.id);
      }

      showNotification('Anexo(s) enviado(s) com sucesso', 'success');
      await loadTransactionAttachments(transactionId);
    } catch (err) {
      showNotification(err.message || 'Erro ao enviar anexo', 'error');
    } finally {
      setTransactionAttachmentsUploading(false);
    }
  };

  const handleOpenTransactionAttachment = async (attachmentId, mode) => {
    const transactionId = selectedTransaction?.id;
    if (!transactionId || !attachmentId) return;
    try {
      const res = await financeService.getTransactionAttachmentSignedUrl(transactionId, attachmentId, mode);
      if (res?.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      showNotification(err.message || 'Erro ao abrir anexo', 'error');
    }
  };

  const handleDeleteTransactionAttachment = async (attachmentId) => {
    const transactionId = selectedTransaction?.id;
    if (!transactionId || !attachmentId) return;
    if (!window.confirm('Remover este anexo?')) return;
    try {
      await financeService.deleteTransactionAttachment(transactionId, attachmentId);
      showNotification('Anexo removido', 'success');
      await loadTransactionAttachments(transactionId);
    } catch (err) {
      showNotification(err.message || 'Erro ao remover anexo', 'error');
    }
  };

  // Função para verificar se pode editar/excluir transação verificada
  const canEditVerifiedTransaction = (transaction) => {
    return !transaction.is_verified || isSuperAdmin();
  };

  useEffect(() => {
    loadData();
    loadEnums();
    loadProducts();
  }, []);

  // Ensure product filter displays a label even when the option isn't loaded yet
  useEffect(() => {
    let cancelled = false;
    const id = String(productFilter ?? '').trim();
    if (!id) {
      if (productFilterLabel) setProductFilterLabel('');
      return;
    }
    if (productFilterLabel) return;

    (async () => {
      const label = await getProductLabelById(id);
      if (!cancelled && label) setProductFilterLabel(label);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter, products, productFilterLabel]);

  // Server-side pagination: reload when page or rowsPerPage or filters change
  useEffect(() => {
    if (!loading) {
      loadTransactionsPage();
      loadTransactionsTotals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, typeFilter, classificationFilter, productFilter, assetFilter, bankFilter, subclassificationFilter, verificationFilter, startDate, endDate, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const assetsData = await withCache(
        'finance_assets_list',
        () => financeService.getAssets(),
        5 * 60 * 1000
      );
      // initial transactions page and totals em paralelo para reduzir latência
      await Promise.all([
        loadTransactionsPage(true),
        loadTransactionsTotals()
      ]);
      setAssets(assetsData);
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionsPage = async (reset = false) => {
    try {
      const isVerified = verificationFilter === 'all' ? undefined : (verificationFilter === 'verified');
      const params = {
        q: searchTerm || '',
        transaction_type: typeFilter || '',
        classification: classificationFilter || '',
        product_id: productFilter || '',
        asset_id: assetFilter || '',
        bank_id: bankFilter || '',
        subclassification_id: subclassificationFilter || '',
        start_date: startDate ? new Date(startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : '',
        end_date: endDate ? new Date(endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : '',
        is_verified: isVerified,
        offset: (reset ? 0 : page * rowsPerPage),
        limit: rowsPerPage
      };
      const resp = await financeService.getTransactionsPage(params);
      const data = resp?.data || [];
      setTransactions(data);
      setTransactionsTotal(resp?.total || data.length || 0);
      if (reset) setPage(0);
    } catch (err) {
      console.error('Erro ao carregar transações paginadas:', err);
      setTransactions([]);
      setTransactionsTotal(0);
    }
  };

  const loadTransactionsTotals = async () => {
    try {
      const isVerified = verificationFilter === 'all' ? undefined : (verificationFilter === 'verified');
      const params = {
        q: searchTerm || '',
        transaction_type: typeFilter || '',
        classification: classificationFilter || '',
        product_id: productFilter || '',
        asset_id: assetFilter || '',
        bank_id: bankFilter || '',
        subclassification_id: subclassificationFilter || '',
        start_date: startDate ? new Date(startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : '',
        end_date: endDate ? new Date(endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : '',
        is_verified: isVerified,
      };
      const response = await financeService.getTransactionsTotals(params);
      // A resposta da API vem como { success: true, total_revenue: ..., total_costs: ..., ... }
      // Extrair os dados corretamente do objeto de resposta
      setGlobalTotals({
        total_revenue: parseFloat(response.total_revenue || 0),
        total_costs: parseFloat(response.total_costs || 0),
        total_expenses: parseFloat(response.total_expenses || 0),
        unverified_count: parseInt(response.unverified_count || 0, 10)
      });
    } catch (err) {
      console.error('Erro ao carregar totais financeiros:', err);
      setGlobalTotals({ total_revenue: 0, total_costs: 0, total_expenses: 0, unverified_count: 0 });
    }
  };

  const buildExportParams = () => {
    const isVerified = verificationFilter === 'all' ? undefined : (verificationFilter === 'verified');
    return {
      q: searchTerm || undefined,
      transaction_type: typeFilter || undefined,
      classification: classificationFilter || undefined,
      product_id: productFilter || undefined,
      asset_id: assetFilter || undefined,
      bank_id: bankFilter || undefined,
      subclassification_id: subclassificationFilter || undefined,
      start_date: startDate ? new Date(startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
      end_date: endDate ? new Date(endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
      is_verified: typeof isVerified === 'boolean' ? isVerified : undefined,
    };
  };

  const handleExportTransactionsPdf = async () => {
    try {
      setExportingTransactions(true);
      await financeService.exportTransactionsReportPdf(buildExportParams());
      showNotification('Relatório PDF exportado com sucesso', 'success');
    } catch (err) {
      console.error('Erro ao exportar relatório PDF de transações:', err);
      showNotification('Erro ao exportar relatório PDF', 'error');
    } finally {
      setExportingTransactions(false);
    }
  };

  const handleExportTransactionsExcel = async () => {
    try {
      setExportingTransactions(true);
      await financeService.exportTransactionsReportExcel(buildExportParams());
      showNotification('Relatório Excel exportado com sucesso', 'success');
    } catch (err) {
      console.error('Erro ao exportar relatório Excel de transações:', err);
      showNotification('Erro ao exportar relatório Excel', 'error');
    } finally {
      setExportingTransactions(false);
    }
  };

  const loadEnums = async () => {
    try {
      // Cache de 1 hora para enums
      const [types, classifications] = await Promise.all([
        withCache('finance_enums_types', () => financeService.getTransactionTypes(), 60 * 60 * 1000),
        withCache('finance_enums_classifications', () => financeService.getTransactionClassifications(), 60 * 60 * 1000)
      ]);
      setEnums({
        transactionTypes: types || [],
        transactionClassifications: classifications || []
      });
    } catch (err) {
      console.error('Erro ao carregar enums:', err);
      setEnums({
        transactionTypes: [],
        transactionClassifications: []
      });
    }
  };

  const loadProducts = async () => {
    try {
      // Respeitar RBAC: somente carrega se tiver permissão
      if (!hasPermission || !hasPermission('finance.products.read')) {
        setProducts([]);
        return;
      }
      // Cache de 10 minutos para produtos
      const productsData = await withCache(
        'finance_products_list',
        () => financeService.getAvailableProducts(),
        10 * 60 * 1000
      );
      setProducts(productsData || []);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setProducts([]);
    }
  };

  const getProductLabelById = async (productId) => {
    try {
      if (!productId) return '';
      // Tentar pelo estado já carregado
      const fromState = (products || []).find(p => (p.product_id || p.id) === productId);
      if (fromState) return fromState.product_title || fromState.title || fromState.name || '';
      // Carregar lista leve de produtos com cache e buscar pelo id
      const list = await withCache(
        'finance_products_list',
        () => financeService.getAvailableProducts(),
        10 * 60 * 1000
      );
      const found = (list || []).find(p => (p.product_id || p.id) === productId);
      return found ? (found.product_title || found.title || found.name || '') : '';
    } catch {
      return '';
    }
  };

  const getSupplierLabelById = async (supplierId) => {
    try {
      if (!supplierId) return '';
      const supplier = await withCache(
        `finance_supplier_${supplierId}`,
        () => financeService.getSupplier(supplierId),
        60 * 60 * 1000
      );
      return supplier?.name || '';
    } catch {
      return '';
    }
  };

  const getBankLabelById = async (bankId) => {
    try {
      if (!bankId) return '';
      const bank = await withCache(
        `finance_bank_${bankId}`,
        () => financeService.getBank(bankId),
        60 * 60 * 1000
      );
      return bank?.code ? `${bank.code} - ${bank.name}` : (bank?.name || '');
    } catch {
      return '';
    }
  };

  const getSubclassificationLabelById = async (subclassificationId) => {
    try {
      if (!subclassificationId) return '';
      const item = await withCache(
        `finance_subclassification_${subclassificationId}`,
        () => financeService.getSubclassification(subclassificationId),
        60 * 60 * 1000
      );
      return item?.name || '';
    } catch {
      return '';
    }
  };

  const handleOpenDialog = async (transaction = null) => {
    setSelectedTransaction(transaction);
    if (transaction) {
      // Labels iniciais e resolução otimizada assíncrona
      setSelectedLabels({
        product: transaction.product_title || transaction.product_name || '',
        asset: transaction.asset_name || '',
        supplier: transaction.supplier_name || '',
        bank: transaction.bank_name || '',
        subclassification: transaction.subclassification_name || ''
      });
      setFormData({
        transaction_date: new Date(transaction.transaction_date),
        due_date: transaction.due_date ? new Date(transaction.due_date) : null,
        payment_date: transaction.payment_date ? new Date(transaction.payment_date) : null,
        payment_status: transaction.payment_status || '',
        amount: transaction.amount.toString(),
        transaction_type: transaction.transaction_type,
        classification: transaction.classification,
        description: transaction.description,
        product_id: transaction.product_id || '',
        asset_id: transaction.asset_id || '',
        supplier_id: transaction.supplier_id || '',
        bank_id: transaction.bank_id || '',
        subclassification_id: transaction.subclassification_id || '',
        notes: transaction.notes || ''
      });
      // Resolver rótulos ausentes (produto/sorteio, fornecedor e banco) com cache
      const [productLabel, supplierLabel, bankLabel, subclassificationLabel] = await Promise.all([
        (!transaction.product_title && transaction.product_id) ? getProductLabelById(transaction.product_id) : Promise.resolve(transaction.product_title || ''),
        (!transaction.supplier_name && transaction.supplier_id) ? getSupplierLabelById(transaction.supplier_id) : Promise.resolve(transaction.supplier_name || ''),
        (!transaction.bank_name && transaction.bank_id) ? getBankLabelById(transaction.bank_id) : Promise.resolve(transaction.bank_name || ''),
        (!transaction.subclassification_name && transaction.subclassification_id) ? getSubclassificationLabelById(transaction.subclassification_id) : Promise.resolve(transaction.subclassification_name || '')
      ]);
      setSelectedLabels(prev => ({
        ...prev,
        product: productLabel || prev.product || '',
        supplier: supplierLabel || prev.supplier || '',
        bank: bankLabel || prev.bank || '',
        subclassification: subclassificationLabel || prev.subclassification || ''
      }));

      setTransactionAttachments([]);
      await loadTransactionAttachments(transaction.id);
    } else {
      setSelectedLabels({ product: '', asset: '', supplier: '', bank: '', subclassification: '' });
      setTransactionAttachments([]);
      setFormData({
        transaction_date: new Date(),
        due_date: null,
        payment_date: null,
        payment_status: '',
        amount: '',
        transaction_type: '',
        classification: '',
        description: '',
        product_id: '',
        asset_id: '',
        supplier_id: '',
        bank_id: '',
        subclassification_id: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTransaction(null);
    setTransactionAttachments([]);
    setTransactionAttachmentsLoading(false);
    setTransactionAttachmentsUploading(false);
    setSelectedLabels({ product: '', asset: '', supplier: '', bank: '', subclassification: '' });
    setFormData({
      transaction_date: new Date(),
      due_date: null,
      payment_date: null,
      payment_status: '',
      amount: '',
      transaction_type: '',
      classification: '',
      description: '',
      product_id: '',
      asset_id: '',
      supplier_id: '',
      bank_id: '',
      subclassification_id: '',
      notes: ''
    });
  };

  const handleSaveTransaction = async () => {
    if (savingTransaction) return;
    try {
      setSavingTransaction(true);
      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: formData.due_date || null,
        payment_date: formData.payment_date || null,
        payment_status: (formData.payment_status || '').trim() || null,
        asset_id: formData.asset_id || null,
        product_id: formData.product_id || null,
        supplier_id: formData.supplier_id || null,
        bank_id: formData.bank_id || null,
        subclassification_id: formData.subclassification_id || null,
        description: (formData.description || '').trim(),
        notes: formData.notes?.trim() || null
      };

      if (selectedTransaction) {
        await financeService.updateTransaction(selectedTransaction.id, transactionData);
        showNotification('Transação atualizada com sucesso', 'success');
      } else {
        await financeService.createTransaction(transactionData);
        showNotification('Transação criada com sucesso', 'success');
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setSavingTransaction(false);
    }
  };

  const handleOpenVerifyDialog = (transaction) => {
    setTransactionToVerify(transaction);
    setVerifyDialogOpen(true);
  };

  const handleCloseVerifyDialog = () => {
    if (verifyingTransaction) return;
    setVerifyDialogOpen(false);
    setTransactionToVerify(null);
  };

  const handleConfirmVerifyTransaction = async () => {
    const id = transactionToVerify?.id;
    if (!id || verifyingTransaction) return;

    try {
      setVerifyingTransaction(true);
      await financeService.verifyTransaction(id);
      showNotification('Transação verificada com sucesso', 'success');
      setVerifyDialogOpen(false);
      setTransactionToVerify(null);
      await Promise.all([loadTransactionsPage(), loadTransactionsTotals()]);
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setVerifyingTransaction(false);
    }
  };
  const handleDeleteTransaction = async (transactionId) => {
    if (window.confirm('Tem certeza que deseja deletar esta transação?')) {
      try {
        await financeService.deleteTransaction(transactionId);
        showNotification('Transação deletada com sucesso', 'success');
        loadData();
      } catch (err) {
        showNotification(err.message, 'error');
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTransactionTypeColor = (type) => {
    return type === 'entrada' ? 'success' : 'error';
  };

  const getClassificationLabel = (classification) => {
    const labels = {
      'custo_fixo': 'Custo Fixo',
      'custo_variavel': 'Custo Variável',
      'despesa_fixa': 'Despesa Fixa',
      'despesa_variavel': 'Despesa Variável',
      'receita': 'Receita',
      'imposto': 'Imposto',
      'retirada_lucros': 'Retirada lucros',
      'ativo_imobilizado': 'Ativo imobilizado'
    };
    return labels[classification] || classification;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Limpar todos os filtros
  const handleClearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setClassificationFilter('');
    setProductFilter('');
    setProductFilterLabel('');
    setAssetFilter('');
    setBankFilter('');
    setBankFilterLabel('');
    setSubclassificationFilter('');
    setSubclassificationFilterLabel('');
    setVerificationFilter('all');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  // Contar filtros ativos
  const activeFiltersCount = [
    typeFilter,
    classificationFilter,
    productFilter,
    assetFilter,
    bankFilter,
    subclassificationFilter,
    verificationFilter !== 'all' ? verificationFilter : '',
    startDate,
    endDate
  ].filter(Boolean).length;

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated data for display
  const paginatedTransactions = transactions;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          {/* Modern Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              borderRadius: 3,
              p: 4,
              mb: 4,
              color: 'white',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
            }}
          >
            <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} spacing={2}>
              <Box>
                <Typography variant={isMobile ? "h5" : "h4"} component="h1" fontWeight="bold" gutterBottom>
                  💰 Gestão Financeira
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Controle completo das transações e fluxo de caixa
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                  minWidth: isMobile ? '100%' : 'auto'
                }}
              >
                Nova Transação
              </Button>
            </Stack>
          </Box>

          {/* Tabs Navigation */}
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 3,
              overflow: 'hidden'
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="fullWidth"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  minHeight: 56,
                  transition: 'all 0.3s ease'
                },
                '& .Mui-selected': {
                  fontWeight: 600,
                  color: theme.palette.primary.main
                }
              }}
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: theme.palette.primary.main
                }
              }}
            >
              <Tab 
                label="Transações" 
                icon={<ReceiptIcon />}
                iconPosition="start"
                sx={{ 
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              />
              <Tab 
                label="Fornecedores" 
                icon={<BusinessIcon />}
                iconPosition="start"
                sx={{ 
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              />
              <Tab 
                label="Bancos" 
                icon={<AccountBalanceIcon />}
                iconPosition="start"
                sx={{ 
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              />
              <Tab 
                label="Subclassificações" 
                icon={<CategoryIcon />}
                iconPosition="start"
                sx={{ 
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              />
            </Tabs>
          </Paper>

          {/* Tab Content: Transações */}
          {currentTab === 0 && (
            <Fade in={currentTab === 0} timeout={300}>
              <Box>
              {/* Search and Filters */}
              <Paper
                elevation={0}
                sx={{
              p: 3,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2
            }}
          >
            {/* Primeira linha - Busca e ações principais */}
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center" mb={2}>
              <TextField
                fullWidth={isMobile}
                placeholder="Buscar transações..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
                sx={{ minWidth: isMobile ? 'auto' : 300 }}
              />
              
              <Badge badgeContent={activeFiltersCount} color="primary">
                <Button
                  variant={showFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  Filtros
                </Button>
              </Badge>

              {activeFiltersCount > 0 && (
                <Chip
                  label="Limpar Filtros"
                  size="small"
                  onDelete={handleClearFilters}
                  color="primary"
                  variant="outlined"
                />
              )}

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                size="small"
              >
                Atualizar
              </Button>

              {hasPermission && hasPermission('finance.reports.read') && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleExportTransactionsPdf}
                    size="small"
                    disabled={exportingTransactions}
                  >
                    Exportar PDF
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleExportTransactionsExcel}
                    size="small"
                    disabled={exportingTransactions}
                  >
                    Exportar Excel
                  </Button>
                </>
              )}
            </Stack>

            {/* Filtros expandidos */}
            {showFilters && (
              <Fade in={showFilters}>
                <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={typeFilter}
                          onChange={(e) => {
                            setTypeFilter(e.target.value);
                            setPage(0);
                          }}
                          label="Tipo"
                        >
                          <MenuItem value="">Todos</MenuItem>
                          {Array.isArray(enums.transactionTypes) && enums.transactionTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.value === 'entrada' ? 'Entrada' : 'Saída'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Classificação</InputLabel>
                        <Select
                          value={classificationFilter}
                          onChange={(e) => {
                            setClassificationFilter(e.target.value);
                            // Subclassificação depende de Classificação: limpa para evitar inconsistência
                            setSubclassificationFilter('');
                            setSubclassificationFilterLabel('');
                            setPage(0);
                          }}
                          label="Classificação"
                        >
                          <MenuItem value="">Todas</MenuItem>
                          {Array.isArray(enums.transactionClassifications) && enums.transactionClassifications.map(classification => (
                            <MenuItem key={classification.value} value={classification.value}>
                              {getClassificationLabel(classification.value)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={verificationFilter}
                          onChange={(e) => {
                            setVerificationFilter(e.target.value);
                            setPage(0);
                          }}
                          label="Status"
                        >
                          <MenuItem value="all">Todas</MenuItem>
                          <MenuItem value="verified">Verificadas</MenuItem>
                          <MenuItem value="pending">Pendentes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <AsyncSearchSelect
                        label="Produto/Sorteio"
                        value={productFilter}
                        selectedLabel={productFilterLabel}
                        onChange={(id, option) => {
                          const nextId = String(id ?? '').trim();
                          setProductFilter(nextId);
                          setProductFilterLabel(option?.label || '');
                          setPage(0);
                        }}
                        fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                        placeholder="Buscar produto..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <AsyncSearchSelect
                        label="Ativo"
                        value={assetFilter}
                        onChange={(id) => { setAssetFilter(id); setPage(0); }}
                        fetchPage={({ q, offset, limit }) => financeService.searchAssets({ q, offset, limit })}
                        placeholder="Buscar ativo..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <AsyncSearchSelect
                        label="Banco"
                        value={bankFilter}
                        selectedLabel={bankFilterLabel}
                        onChange={(id, option) => {
                          const nextId = String(id ?? '').trim();
                          setBankFilter(nextId);
                          setBankFilterLabel(option?.label || '');
                          setPage(0);
                        }}
                        fetchPage={({ q, offset, limit }) => financeService.searchBanks({ q, offset, limit })}
                        placeholder="Buscar banco..."
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <AsyncSearchSelect
                        label="Subclassificação"
                        value={subclassificationFilter}
                        selectedLabel={subclassificationFilterLabel}
                        onChange={(id, option) => {
                          const nextId = String(id ?? '').trim();
                          setSubclassificationFilter(nextId);
                          setSubclassificationFilterLabel(option?.label || '');
                          setPage(0);
                        }}
                        fetchPage={({ q, offset, limit }) => financeService.searchSubclassifications({ q, offset, limit, classification: classificationFilter || undefined })}
                        placeholder={classificationFilter ? 'Buscar subclassificação...' : 'Buscar subclassificação (todas)'}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        label="Data Inicial"
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                          setPage(0);
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        label="Data Final"
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                          setPage(0);
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Fade>
            )}
          </Paper>

          {/* Resumo financeiro modernizado (censurável para quem tem apenas read.self) */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={600}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'success.light',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                        <TrendingUpIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Receita Total
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {hasPermission('finance.transactions.read.all') ? (
                            formatCurrency(globalTotals.total_revenue)
                          ) : (
                            '•••••••'
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={800}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'error.light',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                        <TrendingDownIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Custos Totais
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="error.main">
                          {hasPermission('finance.transactions.read.all') ? (
                            formatCurrency(globalTotals.total_costs)
                          ) : (
                            '•••••••'
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1000}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'warning.light',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                        <AccountBalanceIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Despesas Totais
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                          {hasPermission('finance.transactions.read.all') ? (
                            formatCurrency(globalTotals.total_expenses)
                          ) : (
                            '•••••••'
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1200}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'info.light',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Badge
                        badgeContent={globalTotals.unverified_count}
                        color="error"
                      >
                        <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                          <ReceiptIcon fontSize="large" />
                        </Avatar>
                      </Badge>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Não Verificadas
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="info.main">
                          {globalTotals.unverified_count}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>

          <Paper 
            elevation={0}
            sx={{ 
              width: '100%', 
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                📊 Transações Financeiras
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {transactionsTotal} transações encontradas
              </Typography>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                    {!isMobile && (
                      <TableCell sx={{ fontWeight: 'bold' }}>Banco</TableCell>
                    )}
                    {!isMobile && (
                      <TableCell sx={{ fontWeight: 'bold' }}>Subclassificação</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Classificação</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction, index) => (
                    <TableRow 
                      key={transaction.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'grey.50' },
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <DateIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="body2">
                            {new Date(transaction.transaction_date).toLocaleDateString('pt-BR')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.description}
                          </Typography>
                          {isMobile && (transaction.bank_name || transaction.bank_id) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              🏦 Banco: {transaction.bank_name || ''}
                            </Typography>
                          )}
                          {isMobile && (transaction.subclassification_name || transaction.subclassification_id) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              🏷️ Sub: {transaction.subclassification_name || ''}
                            </Typography>
                          )}
                          {transaction.asset_name && (
                            <Typography variant="caption" color="text.secondary">
                              🏠 Ativo: {transaction.asset_name}
                            </Typography>
                          )}
                          {transaction.product_id && (
                            <Typography variant="caption" color="primary.main" display="block">
                              🎯 Produto: {transaction.product_id}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {!isMobile && (
                        <TableCell sx={{ maxWidth: 220 }}>
                          {transaction.bank_name ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <AccountBalanceIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                              <Tooltip title={transaction.bank_name} placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {transaction.bank_name}
                                </Typography>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      )}

                      {!isMobile && (
                        <TableCell sx={{ maxWidth: 220 }}>
                          {transaction.subclassification_name ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CategoryIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                              <Tooltip title={transaction.subclassification_name} placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {transaction.subclassification_name}
                                </Typography>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          icon={transaction.transaction_type === 'entrada' ? 
                            <TrendingUpIcon fontSize="small" /> : 
                            <TrendingDownIcon fontSize="small" />
                          }
                          label={transaction.transaction_type === 'entrada' ? 'Entrada' : 'Saída'}
                          color={getTransactionTypeColor(transaction.transaction_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getClassificationLabel(transaction.classification)}
                          size="small"
                          sx={{ 
                            bgcolor: 'grey.100',
                            color: 'text.primary'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          color={transaction.transaction_type === 'entrada' ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.is_verified ? 'Verificada' : 'Pendente'}
                          color={transaction.is_verified ? 'success' : 'warning'}
                          size="small"
                          variant={transaction.is_verified ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={
                          transaction.is_verified && !isSuperAdmin() 
                            ? "Transação verificada não pode ser editada" 
                            : "Editar transação"
                        }>
                          <span>
                            <IconButton
                              onClick={() => handleOpenDialog(transaction)}
                              size="small"
                              disabled={!canEditVerifiedTransaction(transaction)}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!transaction.is_verified && (
                          <Tooltip title="Verificar transação">
                            <IconButton
                              onClick={() => handleOpenVerifyDialog(transaction)}
                              size="small"
                              color="success"
                            >
                              <VerifiedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={
                          transaction.is_verified && !isSuperAdmin() 
                            ? "Transação verificada não pode ser excluída" 
                            : "Excluir transação"
                        }>
                          <span>
                            <IconButton
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              size="small"
                              color="error"
                              disabled={!canEditVerifiedTransaction(transaction)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <TablePagination
                component="div"
                count={transactionsTotal}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  '& .MuiTablePagination-toolbar': {
                    padding: '0 16px',
                  }
                }}
              />
            </Box>
          </Paper>
              </Box>
            </Fade>
          )}

          {/* Tab Content: Fornecedores */}
          {currentTab === 1 && (
            <Fade in={currentTab === 1} timeout={300}>
              <Box>
                <React.Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                  </Box>
                }>
                  <SuppliersTab />
                </React.Suspense>
              </Box>
            </Fade>
          )}

          {/* Tab Content: Bancos */}
          {currentTab === 2 && (
            <Fade in={currentTab === 2} timeout={300}>
              <Box>
                <React.Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                  </Box>
                }>
                  <BanksTab />
                </React.Suspense>
              </Box>
            </Fade>
          )}

          {/* Tab Content: Subclassificações */}
          {currentTab === 3 && (
            <Fade in={currentTab === 3} timeout={300}>
              <Box>
                <React.Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                  </Box>
                }>
                  <SubclassificationsTab />
                </React.Suspense>
              </Box>
            </Fade>
          )}

        {/* Confirmação de segurança para verificar transação */}
        <Dialog
          open={verifyDialogOpen}
          onClose={handleCloseVerifyDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirmar verificação</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ao verificar, esta transação será marcada como <strong>verificada</strong>.
              {!isSuperAdmin() && (
                <> Usuários sem permissão de super admin não poderão editar/excluir depois.</>
              )}
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Revise os dados antes de confirmar:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">
                {transactionToVerify?.description || 'Sem descrição'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor: {transactionToVerify ? formatCurrency(transactionToVerify.amount) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data: {transactionToVerify?.transaction_date ? new Date(transactionToVerify.transaction_date).toLocaleDateString('pt-BR') : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tipo: {transactionToVerify?.transaction_type || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Classificação: {transactionToVerify?.classification ? getClassificationLabel(transactionToVerify.classification) : '-'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVerifyDialog} disabled={verifyingTransaction}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmVerifyTransaction}
              color="success"
              variant="contained"
              disabled={!transactionToVerify || verifyingTransaction}
              startIcon={verifyingTransaction ? <CircularProgress size={16} /> : <VerifiedIcon />}
            >
              {verifyingTransaction ? 'Verificando...' : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>

          {/* Dialog para criar/editar transação */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {selectedTransaction ? 'Editar Transação' : 'Nova Transação'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <DateTimePicker
                  label="Data da Transação"
                  value={formData.transaction_date}
                  onChange={(newValue) => setFormData({ ...formData, transaction_date: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />

                <DateTimePicker
                  label="Data de Vencimento (opcional)"
                  value={formData.due_date}
                  onChange={(newValue) => setFormData({ ...formData, due_date: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />

                <DateTimePicker
                  label="Data de Pagamento (opcional)"
                  value={formData.payment_date}
                  onChange={(newValue) => setFormData({ ...formData, payment_date: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
                
                <TextField
                  fullWidth
                  label="Valor"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  margin="normal"
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                    label="Tipo"
                    required
                  >
                    {Array.isArray(enums.transactionTypes) && enums.transactionTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.value === 'entrada' ? 'Entrada' : 'Saída'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Classificação</InputLabel>
                  <Select
                    value={formData.classification}
                    onChange={(e) => {
                      const nextClassification = e.target.value;
                      setFormData({
                        ...formData,
                        classification: nextClassification,
                        subclassification_id: ''
                      });
                      setSelectedLabels(prev => ({ ...prev, subclassification: '' }));
                    }}
                    label="Classificação"
                    required
                  >
                    {Array.isArray(enums.transactionClassifications) && enums.transactionClassifications.map((classification) => (
                      <MenuItem key={classification.value} value={classification.value}>
                        {getClassificationLabel(classification.value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ my: 1 }}>
                  <AsyncSearchSelect
                    label="Subclassificação"
                    value={formData.subclassification_id || ''}
                    onChange={(id, option) => {
                      setFormData({ ...formData, subclassification_id: id || '' });
                      setSelectedLabels(prev => ({ ...prev, subclassification: option?.label || '' }));
                    }}
                    fetchPage={({ q, offset, limit }) => {
                      if (!formData.classification) return Promise.resolve([]);
                      return financeService.searchSubclassifications({ q, offset, limit, classification: formData.classification });
                    }}
                    selectedLabel={selectedLabels.subclassification}
                    placeholder={formData.classification ? 'Buscar subclassificação...' : 'Selecione a classificação primeiro'}
                    disabled={!formData.classification}
                  />
                </Box>
                
                <TextField
                  fullWidth
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  required
                  multiline
                  rows={2}
                />
                
                <Box sx={{ my: 1 }}>
                  <AsyncSearchSelect
                    label="Produto/Sorteio"
                    value={formData.product_id || ''}
                    onChange={(id) => setFormData({ ...formData, product_id: id })}
                    fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                    selectedLabel={selectedLabels.product}
                    placeholder="Buscar produto..."
                  />
                </Box>
                
                <Box sx={{ my: 1 }}>
                  <AsyncSearchSelect
                    label="Ativo Associado"
                    value={formData.asset_id || ''}
                    onChange={(id) => setFormData({ ...formData, asset_id: id })}
                    fetchPage={({ q, offset, limit }) => financeService.searchAssets({ q, offset, limit })}
                    selectedLabel={selectedLabels.asset}
                    placeholder="Buscar ativo..."
                  />
                </Box>
                
                <Box sx={{ my: 1 }}>
                  <AsyncSearchSelect
                    label="Fornecedor"
                    value={formData.supplier_id || ''}
                    onChange={(id) => setFormData({ ...formData, supplier_id: id })}
                    fetchPage={({ q, offset, limit }) => financeService.searchSuppliers({ q, offset, limit })}
                    selectedLabel={selectedLabels.supplier}
                    placeholder="Buscar fornecedor..."
                  />
                </Box>

                <Box sx={{ my: 1 }}>
                  <AsyncSearchSelect
                    label="Banco"
                    value={formData.bank_id || ''}
                    onChange={(id) => setFormData({ ...formData, bank_id: id })}
                    fetchPage={({ q, offset, limit }) => financeService.searchBanks({ q, offset, limit })}
                    selectedLabel={selectedLabels.bank}
                    placeholder="Buscar banco..."
                  />
                </Box>
                
                <TextField
                  fullWidth
                  label="Observações"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                />

                <TextField
                  fullWidth
                  label="Situação (opcional)"
                  value={formData.payment_status}
                  onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                  margin="normal"
                  placeholder="Ex: pendente, pago, atrasado"
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Anexos
                </Typography>

                {!selectedTransaction ? (
                  <Alert severity="info">
                    Salve a transação para anexar arquivos.
                  </Alert>
                ) : (
                  <>
                    <Stack direction={isSmall ? 'column' : 'row'} spacing={1} sx={{ mb: 1, alignItems: isSmall ? 'stretch' : 'center' }}>
                      {canCreateTransactionAttachments && (
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<AttachFileIcon />}
                          disabled={transactionAttachmentsUploading}
                        >
                          {transactionAttachmentsUploading ? 'Enviando...' : 'Adicionar arquivo'}
                          <input type="file" hidden multiple onChange={handleUploadTransactionFiles} />
                        </Button>
                      )}
                      {canReadTransactionAttachments && (
                        <Button
                          variant="text"
                          onClick={() => loadTransactionAttachments(selectedTransaction.id)}
                          disabled={transactionAttachmentsLoading}
                        >
                          Atualizar
                        </Button>
                      )}
                    </Stack>

                    {(transactionAttachmentsLoading || transactionAttachmentsUploading) && (
                      <LinearProgress sx={{ mb: 1 }} />
                    )}

                    {!canReadTransactionAttachments ? (
                      <Alert severity="warning">Você não tem permissão para ver anexos.</Alert>
                    ) : transactionAttachments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum anexo.
                      </Typography>
                    ) : (
                      <List dense>
                        {transactionAttachments.map((a) => (
                          <ListItem
                            key={a.id}
                            divider
                            secondaryAction={
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Visualizar">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenTransactionAttachment(a.id, 'view')}
                                      disabled={!canReadTransactionAttachments}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenTransactionAttachment(a.id, 'download')}
                                      disabled={!canReadTransactionAttachments}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {canDeleteTransactionAttachments && (
                                  <Tooltip title="Remover">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteTransactionAttachment(a.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            }
                          >
                            <ListItemText
                              primary={a.original_filename}
                              secondary={`${formatBytes(a.size_bytes)}${a.content_type ? ` • ${a.content_type}` : ''}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveTransaction} 
                variant="contained"
                disabled={savingTransaction || !formData.amount || !formData.transaction_type || !formData.classification || !formData.description}
              >
                {selectedTransaction ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default FinanceManagement;